import React, { useContext, useEffect, useState } from "react";
import { ElementState, Preview } from "@creatomate/preview";
import { IMAGE_TYPE, VIDEO_TYPE } from "components/stories/video-studio/utils/constants";
import { useTemplateBuilder } from "components/admin/templates/context/TemplateBuilderContext";
import MediaContent from "./MediaContent";
import MediaHeader from "./MediaHeader";
import { deleteSections } from "../subtitle/utils";
import { Context } from "../../animation/AnimationTypes";
import AnimationScreen from "../../animation/AnimationScreen";
import { CreatomateContext } from "../../../contexts/VideoStudioContext";

interface MediaProps {
  preview: Preview;
}

const Media: React.FC<MediaProps> = ({ preview }) => {
  const {
    data,
    handleUpdateData,
    getActiveElement,
    handleAutosaveDraft
  } = useContext(CreatomateContext);
  const { mediaType, subtitles } = data;
  const activeElement = getActiveElement(preview) as ElementState;
  const { ready, state } = preview;
  const { elements } = state || { elements: [] };
  const [uploading, setUploading] = useState(false);
  const [showAnimations, setShowAnimations] = useState(false);
  const isVideoType = activeElement?.source?.type === VIDEO_TYPE;
  const isImageType = activeElement?.source?.type === IMAGE_TYPE;
  const showMediaContent = !showAnimations && (isVideoType || isImageType);

  const { setDynamicElements, setRelativeElements } = useTemplateBuilder();

  useEffect(
    () => {
      if (!isVideoType && !isImageType) setShowAnimations(false);
    }, [isVideoType, isImageType]
  );

  useEffect(
    () => {
      if (ready) {
        const mediaElement = elements.find(
          element => element.source.type === VIDEO_TYPE || element.source.type === IMAGE_TYPE
        );
        if (!isVideoType && !isImageType && mediaElement?.source?.id) {
          preview.setActiveElements([mediaElement.source.id]);
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elements, ready]
  );

  const handleMediaUploaded = async (modifications: Record<string, any>) => {
    await preview.applyModifications(modifications);
    handleUpdateData({ uploadingMedia: false });
  };

  const handleUploadingValue = (value: boolean) => setUploading(value);

  const handleDelete = async () => {
    deleteDynamicElement();
    if (isVideoType && activeElement.source.name?.startsWith("media-")) {
      if (subtitles.length > 0) {
        await deleteSections(subtitles, activeElement, preview, handleAutosaveDraft);
      }

      handleUpdateData({
        blockSubtitles: true, originalReplacedVideo: activeElement.source.source
      });
    }
    // We have to do this because if the duration was set and you delete a video,
    // the duration should change dynamically
    await preview.applyModifications({ duration: null });
  };

  // if the media is deleted, the dynamic element will be deleted too
  const deleteDynamicElement = () => {
    setDynamicElements(
      prev => prev.filter(dynamicElement => dynamicElement.elementId !== activeElement.source.id));

    setRelativeElements(
      prev => prev.filter(
        relativeElement => relativeElement.elementId !== activeElement.source.id
        && relativeElement.relativeToId !== activeElement.source.id));
  };

  return (
    <>
      <MediaHeader
        preview={preview}
        activeElement={activeElement}
        mediaType={mediaType}
        uploading={uploading}
        showBackHeader={showAnimations}
        setShowAnimations={setShowAnimations}
        onDelete={handleDelete}
        onMediaUploaded={handleMediaUploaded}
        onUploadingValue={handleUploadingValue}
      />

      {showAnimations && (
        <AnimationScreen
          context={Context.Media}
          preview={preview}
          activeElement={activeElement}
        />
      )}

      {showMediaContent && (
        <MediaContent
          preview={preview}
          activeElement={activeElement}
          uploading={uploading}
          handleMediaUploaded={handleMediaUploaded}
          handleUploadingValue={handleUploadingValue}
        />
      )}
    </>
  );
};

export default Media;
